// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import {
  Box,
  Button,
  Icon,
  Image,
  OsModal,
  OsStack,
  Skeleton,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  DeleteLearnerMixTopicMutation,
  DeleteLearnerMixTopicMutationVariables,
  SaveLearnerMixTopicMutation,
  SaveLearnerMixTopicMutationVariables,
} from "@outschool/gql-frontend-generated";
import { faHeart, faSparkles, faTrash } from "@outschool/icons";
import { gql, useMutation } from "@outschool/ui-apollo";
import { ErrorMessage } from "@outschool/ui-components-shared";
import { navigate } from "@patched/hookrouter";
import React from "react";
import { useAnalytics } from "use-analytics";

import { learnerRoutes } from "../../lib/Routes";
import { LEARNER_MIX_TOPICS_QUERY } from "./useTopicsForFilter";

const SAVE_LEARNER_MIX_TOPIC_MUTATION = gql`
  mutation SaveLearnerMixTopic($input: SaveLearnerMixTopicInput!) {
    saveLearnerMixTopic(input: $input) {
      uid
    }
  }
`;

const DELETE_LEARNER_MIX_TOPIC_MUTATION = gql`
  mutation DeleteLearnerMixTopic($uid: ID!) {
    deleteLearnerMixTopic(uid: $uid)
  }
`;

export type ModalMixTopic = {
  uid?: string;
  title: string;
  description: string;
  imageUrl: string;
  sourceTopics?: string[];
  isDefault: boolean;
  /** When true, image is still being generated by Leonardo */
  isImageLoading?: boolean;
};

interface MixTopicModalProps {
  topic: ModalMixTopic | null;
  onClose: () => void;
  onSave: () => void;
}

export default function MixTopicModal({
  topic,
  onClose,
  onSave,
}: MixTopicModalProps) {
  const { track } = useAnalytics();
  const [
    deleteLearnerMixTopicMutation,
    { loading: isDeleting, error: deleteError },
  ] = useMutation<
    DeleteLearnerMixTopicMutation,
    DeleteLearnerMixTopicMutationVariables
  >(DELETE_LEARNER_MIX_TOPIC_MUTATION, {
    refetchQueries: [LEARNER_MIX_TOPICS_QUERY],
    awaitRefetchQueries: true,
  });

  const [saveLearnerMixTopicMutation, { loading: isSaving, error: saveError }] =
    useMutation<
      SaveLearnerMixTopicMutation,
      SaveLearnerMixTopicMutationVariables
    >(SAVE_LEARNER_MIX_TOPIC_MUTATION, {
      refetchQueries: [LEARNER_MIX_TOPICS_QUERY],
      awaitRefetchQueries: true,
    });

  const handleTakeQuiz = async () => {
    if (!topic || topic.isImageLoading) {
      return;
    }
    if (!topic.uid) {
      await saveLearnerMixTopicMutation({
        variables: {
          input: {
            title: topic.title,
            description: topic.description,
            sourceTopics: topic.sourceTopics ?? [],
            imageUrl: topic.imageUrl,
          },
        },
      });
      onSave();
    }
    track("mixup_machine_take_quiz_click", {
      topicTitle: topic.title,
      sourceTopics: topic.sourceTopics,
      ...(topic.uid ? { topicUid: topic.uid } : {}),
    });
    navigate(learnerRoutes.quizPath(), false, {
      topic: topic.title.trim(),
    });
    onClose();
  };

  const takeQuizButton = (
    <Button
      onClick={handleTakeQuiz}
      startIcon={<Icon icon={faSparkles} />}
      disabled={isSaving || isDeleting || !!topic?.isImageLoading}
      fullWidth
      variant="contained"
    >
      Take a Quiz
    </Button>
  );

  let actionPrimary;
  if (topic?.isDefault) {
    actionPrimary = takeQuizButton;
  } else if (topic?.uid) {
    actionPrimary = (
      <Button
        onClick={async () => {
          await deleteLearnerMixTopicMutation({
            variables: { uid: topic.uid! },
          });
          track("mixup_machine_delete_topic_click", {
            topicUid: topic.uid,
            topicTitle: topic.title,
          });
          onClose();
        }}
        startIcon={<Icon icon={faTrash} />}
        disabled={isDeleting}
        fullWidth
        variant="contained"
      >
        Remove
      </Button>
    );
  } else if (topic) {
    actionPrimary = (
      <Button
        onClick={async () => {
          await saveLearnerMixTopicMutation({
            variables: {
              input: {
                title: topic.title,
                description: topic.description,
                sourceTopics: topic.sourceTopics ?? [],
                imageUrl: topic.imageUrl,
              },
            },
          });
          track("mixup_machine_save_topic_click", {
            topicTitle: topic.title,
            sourceTopics: topic.sourceTopics,
          });
          onSave();
          onClose();
        }}
        startIcon={<Icon icon={faHeart} />}
        disabled={isSaving || topic.isImageLoading}
        fullWidth
        variant="contained"
      >
        {topic.isImageLoading ? "Generating Image..." : "Save This Mix"}
      </Button>
    );
  }

  let actionSecondary;
  if (topic && !topic.isDefault) {
    actionSecondary = takeQuizButton;
  }

  const hasFooterButtons = Boolean(actionPrimary) || Boolean(actionSecondary);
  const hasTwoFooterButtons = Boolean(actionPrimary && actionSecondary);

  // Avoid using the action button props of the OsModal so our style isn't overridden
  const belowActionButtonsContent = hasFooterButtons ? (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        gap: theme.spacing(16),
        [theme.breakpoints.down("sm")]: hasTwoFooterButtons && {
          flexDirection: "column",
          alignItems: "stretch",
        },
      })}
    >
      {actionSecondary}
      {actionPrimary}
    </Box>
  ) : undefined;

  return (
    <OsModal
      open={!!topic}
      onClose={onClose}
      title={topic?.title}
      belowActionButtonsContent={belowActionButtonsContent}
    >
      <ErrorMessage value={deleteError || saveError} />
      <OsStack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
      >
        {topic?.isImageLoading ? (
          <Box
            sx={(theme: Theme) => ({
              borderRadius: "0.5em",
              width: "100%",
              aspectRatio: "1.5",
              overflow: "hidden",
              [theme.breakpoints.up("sm")]: {
                width: 192,
                height: 128,
                flexShrink: 0,
              },
            })}
          >
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
            />
          </Box>
        ) : (
          <Image
            src={topic?.imageUrl}
            alt={topic?.title}
            sx={(theme: Theme) => ({
              objectFit: "cover",
              borderRadius: "0.5em",
              width: "100%",
              aspectRatio: "1.5",
              [theme.breakpoints.up("sm")]: {
                width: 192,
                height: 128,
              },
            })}
          />
        )}
        <Typography variant="body1">{topic?.description}</Typography>
      </OsStack>
    </OsModal>
  );
}
